import { autorun } from 'mobx';
import { stores } from 'stores';
import { setUndefinedToFalse } from 'common/utils/common.utils';
import { toast } from 'react-toastify';
import { DISONA_PARTNER } from 'common/constants/trading.constants';
import SuccessNotificationSound from 'themes/disonaTheme/audio/success-notification-sound.mp3';
import FailureNotificationSound from 'themes/disonaTheme/audio/failure-notification-sound.mp3';
import NewMessageSound from 'themes/disonaTheme/audio/new-message-sound.mp3';
import { appConfig } from '../../config';

/**
 * @typedef {Object} Config
 * @property {boolean} notificationSound
 * @property {boolean} desktopNotifications
 * @property {Notify} notify
 */

/**
 * @typedef {Object} Notify
 * @property {boolean} automatedDeals
 * @property {boolean} automatedPositionEdits
 * @property {boolean} manualDeals
 * @property {boolean} manualPositionEdits
 */

const NewMessageAudio = new Audio(NewMessageSound);
const SuccessNotificationAudio = new Audio(SuccessNotificationSound);
SuccessNotificationAudio.volume = 0.5;
const FailureNotificationAudio = new Audio(FailureNotificationSound);
FailureNotificationAudio.volume = 0.8;

class NotificationService {
  #whiteLabelPartnerName = appConfig.whiteLabelPartner;

  #setConfigFromMetaData() {
    const { metaData } = stores.ProfileStore;
    const notificationsMetaData = metaData?.notifications;
    const onSiteNotificationsMetaData =
      notificationsMetaData?.onSiteNotifications;

    this.config.notificationSound = setUndefinedToFalse(
      metaData?.notificationSound
    );
    this.config.desktopNotifications = setUndefinedToFalse(
      metaData?.desktopNotifications
    );
    this.config.notify.automatedDeals = setUndefinedToFalse(
      onSiteNotificationsMetaData?.notifyOnSiteAboutAutomatedDeals
    );
    this.config.notify.automatedPositionEdits = setUndefinedToFalse(
      onSiteNotificationsMetaData?.notifyOnSiteAboutAutomatedPositionEdits
    );
    this.config.notify.manualDeals = setUndefinedToFalse(
      onSiteNotificationsMetaData?.notifyOnSiteAboutManualDeals
    );
    this.config.notify.manualPositionEdits = setUndefinedToFalse(
      onSiteNotificationsMetaData?.notifyOnSiteAboutManualPositionEdits
    );
  }

  #handleBeforeUnload = (event) => {
    event.preventDefault();
    return (event.returnValue = 'You have unsaved entered information');
  };

  #playNewMessageSound() {
    NewMessageAudio.play();
  }

  #playErrorSound() {
    FailureNotificationAudio.play();
  }

  #playSuccessSound() {
    SuccessNotificationAudio.play();
  }

  constructor() {
    /** @type {Config} */
    this.config = {
      notificationSound: false,
      desktopNotifications: false,
      notify: {
        automatedDeals: true,
        automatedPositionEdits: true,
        manualDeals: true,
        manualPositionEdits: true,
      },
    };
    /** @type {boolean} */
    this.desktopNotificationsAllowed =
      'Notification' in window ? Notification.permission === 'granted' : false;
  }

  init() {
    autorun(() => {
      if (
        stores.ProfileStore.isDataFetched &&
        this.#whiteLabelPartnerName === DISONA_PARTNER
      ) {
        this.#setConfigFromMetaData();
      }
    });
  }

  warnOnPageClose() {
    window.addEventListener('beforeunload', this.#handleBeforeUnload, {
      capture: true,
    });
  }

  disableWarnOnPageClose() {
    window.removeEventListener('beforeunload', this.#handleBeforeUnload, {
      capture: true,
    });
  }

  requestDesktopNotifications() {
    if (!this.desktopNotificationsAllowed && 'Notification' in window) {
      Notification.requestPermission().then((response) => {
        this.desktopNotificationsAllowed = response === 'granted';
      });
    }
  }

  /**
   * @param {string} message
   */
  error(message) {
    const errorNotificationOptions = {
      containerId: this.#whiteLabelPartnerName,
      onOpen: this.config.notificationSound ? this.#playErrorSound : () => {},
    };
    toast.error(message, errorNotificationOptions);
  }

  /**
   * @param {string} message
   */
  success(message) {
    const successNotificationOptions = {
      containerId: this.#whiteLabelPartnerName,
      onOpen: this.config.notificationSound ? this.#playSuccessSound : () => {},
    };
    toast.success(message, successNotificationOptions);
  }
}

export const Notifier = new NotificationService();
